import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  styled,
  Typography,
} from '@mui/material';
import { useLayoutContext } from 'contexts/LayoutContext';
import { IncomeOrExpenseApportionmentMode } from 'further-types/lp/income-and-expenses';
import { LpAccrualFrequency } from 'further-types/lp/lp-fee-template';
import BasicSelect from 'further-ui/components/BasicSelect';
import DatePicker from 'further-ui/components/DatePicker';
import { Column, Row } from 'further-ui/components/Layout';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormValues } from '../FormWrapper';

const DateField = styled(Box)({
  flex: 1,
});

const basisOptions = [
  { value: LpAccrualFrequency.MonthlyAdvance, label: 'Monthly' },
  { value: LpAccrualFrequency.QuarterlyAdvance, label: 'Quarterly' },
  { value: LpAccrualFrequency.BiannualyAdvance, label: 'Biannual' },
  { value: LpAccrualFrequency.AnnuallyAdvance, label: 'Annual' },
];

const LedgerDateInput: React.FC = () => {
  const { control, watch, resetField } = useFormContext<FormValues>();
  const { selectedVehicleId } = useLayoutContext();
  const { latestApprovedEqualisationDate } = useEqualisations(
    selectedVehicleId ?? '',
  );
  const mode =
    watch('apportionmentMode') || IncomeOrExpenseApportionmentMode.Single;

  const handleModeChange = (newMode: IncomeOrExpenseApportionmentMode) => {
    if (newMode === IncomeOrExpenseApportionmentMode.Single) {
      resetField('apportionmentBasis');
      resetField('endDate');
    } else if (newMode === IncomeOrExpenseApportionmentMode.Multiple) {
      resetField('apportionmentBasis');
      resetField('endDate');
    }
  };

  return (
    <Column spacing="sm">
      <Controller
        name="apportionmentMode"
        control={control}
        render={({ field }) => (
          <RadioGroup row {...field}>
            <Row>
              <FormControlLabel
                value="single"
                control={
                  <Radio
                    checked={
                      field.value === IncomeOrExpenseApportionmentMode.Single
                    }
                    onChange={(e) => {
                      field.onChange(e);
                      handleModeChange(IncomeOrExpenseApportionmentMode.Single);
                    }}
                  />
                }
                label={<Typography variant="body2">Single date</Typography>}
              />
              <FormControlLabel
                value="multiple"
                control={
                  <Radio
                    checked={
                      field.value === IncomeOrExpenseApportionmentMode.Multiple
                    }
                    onChange={(e) => {
                      field.onChange(e);
                      handleModeChange(
                        IncomeOrExpenseApportionmentMode.Multiple,
                      );
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    Apportion across multiple dates
                  </Typography>
                }
              />
            </Row>
          </RadioGroup>
        )}
      />

      {mode === IncomeOrExpenseApportionmentMode.Single && (
        <Controller
          name="startDate"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <DatePicker
              {...field}
              value={field.value ?? null}
              error={!!error}
              helperText={error?.message?.toString()}
              fullWidth
              placeholder="Select date"
              minDate={latestApprovedEqualisationDate}
            />
          )}
        />
      )}

      {mode === IncomeOrExpenseApportionmentMode.Multiple && (
        <>
          <Controller
            name="apportionmentBasis"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <BasicSelect
                {...field}
                options={basisOptions}
                placeholder="Select apportionment basis"
                fullWidth={false}
                error={!!error}
                helperText={error?.message?.toString()}
              />
            )}
          />
          <Row spacing="sm" centered>
            <DateField>
              <Controller
                name="startDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    {...field}
                    value={field.value ?? null}
                    error={!!error}
                    helperText={error?.message?.toString()}
                    fullWidth
                    placeholder="Start date"
                    minDate={latestApprovedEqualisationDate}
                  />
                )}
              />
            </DateField>
            <DateField>
              <Controller
                name="endDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    {...field}
                    value={field.value ?? null}
                    error={!!error}
                    helperText={error?.message?.toString()}
                    fullWidth
                    placeholder="End date"
                    minDate={latestApprovedEqualisationDate}
                  />
                )}
              />
            </DateField>
          </Row>
        </>
      )}
    </Column>
  );
};

export default LedgerDateInput;
